import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

const EditUserModal = ({ isOpen, onClose, user, section, onUpdate }) => {
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);
    
    // Location states for address
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    
    // Birth country states
    const [birthCountries, setBirthCountries] = useState([]);
    
    // Birth state and city states
    const [birthStates, setBirthStates] = useState([]);
    const [birthCities, setBirthCities] = useState([]);
    const [loadingBirthStates, setLoadingBirthStates] = useState(false);
    const [loadingBirthCities, setLoadingBirthCities] = useState(false);
    
    // Dropdown data from API
    const [dropdowns, setDropdowns] = useState({
        caste: [],
        gotra: [],
        rashi: [],
        children_status: []
    });
    
    // Rashi options
    const [rashiOptions, setRashiOptions] = useState([]);
    
    // Education dropdowns
    const [educationOptions, setEducationOptions] = useState([]);
    const [professionOptions, setProfessionOptions] = useState([]);
    
    // Generate height options (4ft to 6ft 11in)
    const generateHeightOptions = () => {
        const heights = [];
        for (let ft = 4; ft <= 6; ft++) {
            for (let inch = 0; inch <= 11; inch++) {
                const totalInches = (ft * 12) + inch;
                const displayValue = `${ft}ft ${inch}in`;
                heights.push({ value: totalInches, label: displayValue });
            }
        }
        return heights;
    };
    
    // Generate weight options (30kg to 150kg)
    const generateWeightOptions = () => {
        const weights = [];
        for (let kg = 30; kg <= 150; kg++) {
            weights.push({ value: kg, label: `${kg} kg` });
        }
        return weights;
    };
    
    const heightOptions = generateHeightOptions();
    const weightOptions = generateWeightOptions();
    
    // Monthly Income Options
    const monthlyIncomeOptions = [
        { value: '0-10000', label: 'Rs0 - Rs10,000' },
        { value: '10000-20000', label: 'Rs10,000 - Rs20,000' },
        { value: '20000-30000', label: 'Rs20,000 - Rs30,000' },
        { value: '30000-40000', label: 'Rs30,000 - Rs40,000' },
        { value: '40000-50000', label: 'Rs40,000 - Rs50,000' },
        { value: '50000-60000', label: 'Rs50,000 - Rs60,000' },
        { value: '60000-70000', label: 'Rs60,000 - Rs70,000' },
        { value: '70000-80000', label: 'Rs70,000 - Rs80,000' },
        { value: '80000-90000', label: 'Rs80,000 - Rs90,000' },
        { value: '90000-100000', label: 'Rs90,000 - Rs1,00,000' },
        { value: '100000-150000', label: 'Rs1,00,000 - Rs1,50,000' },
        { value: '150000-200000', label: 'Rs1,50,000 - Rs2,00,000' },
        { value: '200000-300000', label: 'Rs2,00,000 - Rs3,00,000' },
        { value: '300000-500000', label: 'Rs3,00,000 - Rs5,00,000' },
        { value: '500000-1000000', label: 'Rs5,00,000 - Rs10,00,000' },
        { value: '1000000+', label: 'Rs10,00,000+' }
    ];
    
    // Children Status Options
    const childrenStatusOptions = [
        { value: 'No Children', label: 'No Children' },
        { value: 'Adult Children', label: 'Adult Children' },
        { value: 'Living Separately', label: 'Living Separately' },
        { value: 'Living with x-spouse', label: 'Living with x-spouse' },
        { value: 'Living with me', label: 'Living with me' }
    ];

    useEffect(() => {
        if (user && Object.keys(user).length > 0) {
            setFormData(user);
        }
    }, [user]);

    useEffect(() => {
        // Fetch dropdowns based on section
        if (section === 'personal') {
            fetchAllDropdowns();
            fetchRashiOptions();
            fetchBirthCountries();
            // Fetch birth states if birth_location exists
            if (formData?.birth_location && formData.birth_location !== '') {
                fetchBirthStates(formData.birth_location);
            }
        }
        if (section === 'contact') {
            fetchCountries();
        }
        if (section === 'education') {
            fetchEducationDropdowns();
        }
    }, [section]);

    // Fetch birth states when birth country changes
    useEffect(() => {
        if (section === 'personal' && formData && formData.birth_location && formData.birth_location !== '') {
            fetchBirthStates(formData.birth_location);
        } else {
            setBirthStates([]);
        }
    }, [formData?.birth_location, section]);

    // Fetch birth cities when birth state changes
    useEffect(() => {
        if (section === 'personal' && formData && formData.birth_state && formData.birth_state !== '') {
            fetchBirthCities(formData.birth_state);
        } else {
            setBirthCities([]);
        }
    }, [formData?.birth_state, section]);

    // Fetch states for address when country ID is available
    useEffect(() => {
        if (section === 'contact' && formData && formData.country && formData.country !== '') {
            fetchStates(formData.country);
        }
    }, [formData?.country, section]);

    // Fetch cities for address when state ID is available
    useEffect(() => {
        if (section === 'contact' && formData && formData.state && formData.state !== '') {
            fetchCities(formData.state);
        }
    }, [formData?.state, section]);

    // Fetch Rashi options
    const fetchRashiOptions = async () => {
        try {
            const response = await axios.get('https://app.bihebari.com.np/api/rashi');
            if (response.data && response.data.success) {
                setRashiOptions(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching rashi options:', error);
            // Fallback options
            setRashiOptions([
                { id: 1, name: 'Mesh (Aries)' },
                { id: 2, name: 'Vrishabha (Taurus)' },
                { id: 3, name: 'Mithun (Gemini)' },
                { id: 4, name: 'Karka (Cancer)' },
                { id: 5, name: 'Simha (Leo)' },
                { id: 6, name: 'Kanya (Virgo)' },
                { id: 7, name: 'Tula (Libra)' },
                { id: 8, name: 'Vrishchik (Scorpio)' },
                { id: 9, name: 'Dhanu (Sagittarius)' },
                { id: 10, name: 'Makara (Capricorn)' },
                { id: 11, name: 'Kumbha (Aquarius)' },
                { id: 12, name: 'Meena (Pisces)' }
            ]);
        }
    };

    // Fetch birth countries
    const fetchBirthCountries = async () => {
        try {
            const response = await axios.get('https://app.bihebari.com.np/api/locations/countries');
            if (response.data && response.data.success) {
                setBirthCountries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching birth countries:', error);
        }
    };

    // Fetch birth states
    const fetchBirthStates = async (countryId) => {
        setLoadingBirthStates(true);
        try {
            const response = await axios.get(`https://app.bihebari.com.np/api/locations/states/${countryId}`);
            if (response.data && response.data.success) {
                setBirthStates(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching birth states:', error);
        } finally {
            setLoadingBirthStates(false);
        }
    };

    // Fetch birth cities
    const fetchBirthCities = async (stateId) => {
        setLoadingBirthCities(true);
        try {
            const response = await axios.get(`https://app.bihebari.com.np/api/locations/cities/${stateId}`);
            if (response.data && response.data.success) {
                setBirthCities(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching birth cities:', error);
        } finally {
            setLoadingBirthCities(false);
        }
    };

    const fetchAllDropdowns = async () => {
        setLoadingDropdowns(true);
        try {
            const response = await axios.get('https://app.bihebari.com.np/api/dropdowns');
            if (response.data && response.data.success) {
                const filteredChildrenStatus = (response.data.data.children_status || []).filter(item => 
                    item.status_name !== 'Not Applicable' && item.name !== 'Not Applicable'
                );
                
                setDropdowns({
                    caste: response.data.data.caste || [],
                    gotra: response.data.data.gotra || [],
                    rashi: response.data.data.rashi || [],
                    children_status: filteredChildrenStatus
                });
            }
        } catch (error) {
            console.error('Error fetching dropdowns:', error);
            toast.error('Error loading form data');
        } finally {
            setLoadingDropdowns(false);
        }
    };

    const fetchEducationDropdowns = async () => {
        setLoadingDropdowns(true);
        try {
            const response = await axios.get('https://app.bihebari.com.np/api/education/dropdowns');
            if (response.data && response.data.success) {
                setEducationOptions(response.data.data.education || []);
                setProfessionOptions(response.data.data.professions || []);
            }
        } catch (error) {
            console.error('Error fetching education dropdowns:', error);
            toast.error('Error loading form data');
        } finally {
            setLoadingDropdowns(false);
        }
    };

    const fetchCountries = async () => {
        try {
            const response = await axios.get('https://app.bihebari.com.np/api/locations/countries');
            if (response.data && response.data.success) {
                setCountries(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };

    const fetchStates = async (countryId) => {
        setLoadingStates(true);
        try {
            const response = await axios.get(`https://app.bihebari.com.np/api/locations/states/${countryId}`);
            if (response.data && response.data.success) {
                setStates(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching states:', error);
        } finally {
            setLoadingStates(false);
        }
    };

    const fetchCities = async (stateId) => {
        setLoadingCities(true);
        try {
            const response = await axios.get(`https://app.bihebari.com.np/api/locations/cities/${stateId}`);
            if (response.data && response.data.success) {
                setCities(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
        } finally {
            setLoadingCities(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (!name) return;
        
        // For birth country - reset birth state and birth city
        if (name === 'birth_location') {
            setFormData(prev => ({ ...prev, birth_location: value, birth_state: '', birth_city: '' }));
        }
        // For birth state - reset birth city
        else if (name === 'birth_state') {
            setFormData(prev => ({ ...prev, birth_state: value, birth_city: '' }));
        }
        // For birth city
        else if (name === 'birth_city') {
            setFormData(prev => ({ ...prev, birth_city: value }));
        }
        // For horoscope/rashi
        else if (name === 'horscope') {
            setFormData(prev => ({ ...prev, horscope: value }));
        }
        // For caste - store the name as string
        else if (name === 'caste') {
            const selectedCaste = dropdowns.caste?.find(c => (c.id === parseInt(value) || c.caste_id === parseInt(value)));
            setFormData(prev => ({ ...prev, caste: selectedCaste ? (selectedCaste.caste_name || selectedCaste.name) : value }));
        }
        // For gotra - store the name as string
        else if (name === 'gotra') {
            const selectedGotra = dropdowns.gotra?.find(g => (g.id === parseInt(value) || g.gotra_id === parseInt(value)));
            setFormData(prev => ({ ...prev, gotra: selectedGotra ? (selectedGotra.gotra_name || selectedGotra.name) : value }));
        }
        // For children status
        else if (name === 'children_status') {
            setFormData(prev => ({ ...prev, children_status: value }));
        }
        // For education - store the level as string
        else if (name === 'education') {
            const selectedEducation = educationOptions.find(e => e.id === parseInt(value));
            setFormData(prev => ({ ...prev, education: selectedEducation ? selectedEducation.name : value }));
        }
        // For occupation - store the name as string
        else if (name === 'occupation') {
            const selectedOccupation = professionOptions.find(o => o.id === parseInt(value));
            setFormData(prev => ({ ...prev, occupation: selectedOccupation ? selectedOccupation.name : value }));
        }
        // For monthly income - store as is
        else if (name === 'monthaly_income') {
            setFormData(prev => ({ ...prev, monthaly_income: value }));
        }
        // For address country - store the ID (number)
        else if (name === 'country') {
            setFormData(prev => ({ ...prev, country: value, state: '', city: '' }));
            if (value) {
                fetchStates(value);
            } else {
                setStates([]);
                setCities([]);
            }
        }
        // For address state - store the ID (number), then clear city
        else if (name === 'state') {
            setFormData(prev => ({ ...prev, state: value, city: '' }));
            if (value) {
                fetchCities(value);
            } else {
                setCities([]);
            }
        }
        // For address city - store the NAME as string
        else if (name === 'city') {
            const selectedCity = cities.find(c => c.id === parseInt(value));
            setFormData(prev => ({ ...prev, city: selectedCity ? selectedCity.name : value }));
        }
        else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };
    
    // Helper functions to get selected ID from stored name
    const getSelectedCasteId = () => {
        if (!formData?.caste) return '';
        const caste = dropdowns.caste?.find(c => (c.caste_name || c.name) === formData.caste);
        return caste ? (caste.id || caste.caste_id) : '';
    };
    
    const getSelectedGotraId = () => {
        if (!formData?.gotra) return '';
        const gotra = dropdowns.gotra?.find(g => (g.gotra_name || g.name) === formData.gotra);
        return gotra ? (gotra.id || gotra.gotra_id) : '';
    };
    
    const getSelectedRashiValue = () => {
        return formData?.horscope || '';
    };
    
    const getSelectedChildrenStatusValue = () => {
        return formData?.children_status || '';
    };
    
    const getSelectedEducationId = () => {
        if (!formData?.education) return '';
        const education = educationOptions.find(e => e.name === formData.education);
        return education ? education.id : '';
    };
    
    const getSelectedOccupationId = () => {
        if (!formData?.occupation) return '';
        const occupation = professionOptions.find(o => o.name === formData.occupation);
        return occupation ? occupation.id : '';
    };
    
    // For address location dropdowns
    const getSelectedCountryId = () => {
        return formData?.country || '';
    };
    
    const getSelectedStateId = () => {
        return formData?.state || '';
    };
    
    const getSelectedCityId = () => {
        if (!formData?.city) return '';
        const city = cities.find(c => c.name === formData.city);
        return city ? city.id : '';
    };
    
    // For birth country dropdown
    const getSelectedBirthCountryId = () => {
        return formData?.birth_location || '';
    };
    
    // For birth state dropdown
    const getSelectedBirthStateId = () => {
        return formData?.birth_state || '';
    };
    
    // For birth city dropdown
    const getSelectedBirthCityId = () => {
        return formData?.birth_city || '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user || !user.id) {
            toast.error('User information missing');
            return;
        }
        
        setLoading(true);
        
        try {
            const token = localStorage.getItem('adminToken');
            const endpoint = `https://app.bihebari.com.np/api/admin/users/${user.id}/${section}`;
            
            const submitData = { ...formData };
            
            await axios.put(endpoint, submitData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} details updated successfully`);
            if (onUpdate) onUpdate();
            if (onClose) onClose();
        } catch (error) {
            console.error('Error updating details:', error.response?.data);
            toast.error(error.response?.data?.message || 'Error updating details');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const renderPersonalForm = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="form-label">Full Name</label>
                <input type="text" name="name" value={formData?.name || ''} onChange={handleChange} className="form-input" required />
            </div>
            <div>
                <label className="form-label">Gender</label>
                <select name="gender" value={formData?.gender || ''} onChange={handleChange} className="form-input" required>
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="Other">Other</option>
                </select>
            </div>
            <div>
                <label className="form-label">Date of Birth</label>
                <input type="date" name="dob" value={formData?.dob || ''} onChange={handleChange} className="form-input" />
            </div>
            <div>
                <label className="form-label">Birth Time</label>
                <input type="time" name="birth_time" value={formData?.birth_time || ''} onChange={handleChange} className="form-input" />
            </div>
            
            {/* Birth Country Field */}
            <div>
                <label className="form-label">Birth Country</label>
                <select name="birth_location" value={getSelectedBirthCountryId()} onChange={handleChange} className="form-input">
                    <option value="">Select Birth Country</option>
                    {birthCountries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>
            
            {/* Birth State Field */}
            <div>
                <label className="form-label">Birth State</label>
                <select 
                    name="birth_state" 
                    value={getSelectedBirthStateId()} 
                    onChange={handleChange} 
                    className="form-input"
                    disabled={!formData?.birth_location || loadingBirthStates}
                >
                    <option value="">Select Birth State</option>
                    {birthStates.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {loadingBirthStates && <p className="text-xs text-gray-500 mt-1">Loading states...</p>}
            </div>
            
            {/* Birth City Field */}
            <div>
                <label className="form-label">Birth City</label>
                <select 
                    name="birth_city" 
                    value={getSelectedBirthCityId()} 
                    onChange={handleChange} 
                    className="form-input"
                    disabled={!formData?.birth_state || loadingBirthCities}
                >
                    <option value="">Select Birth City</option>
                    {birthCities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {loadingBirthCities && <p className="text-xs text-gray-500 mt-1">Loading cities...</p>}
            </div>
            
            {/* Horoscope/Rashi Dropdown */}
            <div>
                <label className="form-label">Horoscope / Rashi</label>
                <select name="horscope" value={getSelectedRashiValue()} onChange={handleChange} className="form-input">
                    <option value="">Select Rashi</option>
                    {rashiOptions.map(rashi => (
                        <option key={rashi.id} value={rashi.name}>{rashi.name}</option>
                    ))}
                </select>
            </div>
            
            <div>
                <label className="form-label">Caste</label>
                <select name="caste" value={getSelectedCasteId()} onChange={handleChange} className="form-input">
                    <option value="">Select Caste</option>
                    {dropdowns.caste && dropdowns.caste.map((item) => (
                        <option key={item.id || item.caste_id} value={item.id || item.caste_id}>
                            {item.caste_name || item.name}
                        </option>
                    ))}
                </select>
            </div>
            <div>
                <label className="form-label">Gotra</label>
                <select name="gotra" value={getSelectedGotraId()} onChange={handleChange} className="form-input">
                    <option value="">Select Gotra</option>
                    {dropdowns.gotra && dropdowns.gotra.map((item) => (
                        <option key={item.id || item.gotra_id} value={item.id || item.gotra_id}>
                            {item.gotra_name || item.name}
                        </option>
                    ))}
                </select>
            </div>
            <div>
                <label className="form-label">Marital Status</label>
                <select name="marital" value={formData?.marital || ''} onChange={handleChange} className="form-input">
                    <option value="">Select</option>
                    <option value="Unmarried">Unmarried</option>
                    <option value="Divorce">Divorced</option>
                    <option value="Widowed">Widow/Widower</option>
                </select>
            </div>
            <div>
                <label className="form-label">Children</label>
                <input type="number" name="children" value={formData?.children || 0} onChange={handleChange} className="form-input" />
            </div>
            <div>
                <label className="form-label">Children Status</label>
                <select name="children_status" value={getSelectedChildrenStatusValue()} onChange={handleChange} className="form-input">
                    <option value="">Select Children Status</option>
                    {childrenStatusOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="form-label">Food Preference</label>
                <select name="eat_status" value={formData?.eat_status || ''} onChange={handleChange} className="form-input">
                    <option value="">Select</option>
                    <option value="vegetarian">Vegetarian</option>
                    <option value="non-vegetarian">Non-Vegetarian</option>
                </select>
            </div>
            <div>
                <label className="form-label">Mother's Surname</label>
                <input type="text" name="moms_surname" value={formData?.moms_surname || ''} onChange={handleChange} className="form-input" />
            </div>
            <div className="md:col-span-2">
                <label className="form-label">About Me</label>
                <textarea name="about_me" value={formData?.about_me || ''} onChange={handleChange} rows="3" className="form-input"></textarea>
            </div>
        </div>
    );

    const renderContactForm = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="form-label">Country</label>
                <select name="country" value={getSelectedCountryId()} onChange={handleChange} className="form-input">
                    <option value="">Select Country</option>
                    {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>
            <div>
                <label className="form-label">State</label>
                <select name="state" value={getSelectedStateId()} onChange={handleChange} className="form-input" disabled={!formData?.country || loadingStates}>
                    <option value="">Select State</option>
                    {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {loadingStates && <p className="text-xs text-gray-500 mt-1">Loading states...</p>}
            </div>
            <div>
                <label className="form-label">City</label>
                <select name="city" value={getSelectedCityId()} onChange={handleChange} className="form-input" disabled={!formData?.state || loadingCities}>
                    <option value="">Select City</option>
                    {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {loadingCities && <p className="text-xs text-gray-500 mt-1">Loading cities...</p>}
            </div>
            <div>
                <label className="form-label">District</label>
                <input type="text" name="distric" value={formData?.distric || ''} onChange={handleChange} className="form-input" />
            </div>
            <div>
                <label className="form-label">Residence Status</label>
                <select name="residence_status" value={formData?.residence_status || ''} onChange={handleChange} className="form-input">
                    <option value="">Select</option>
                    <option value="citizenship">Citizenship</option>
                    <option value="tr">TR</option>
                    <option value="pr">PR</option>
                    <option value="other">Other</option>
                </select>
            </div>
            <div>
                <label className="form-label">Email</label>
                <input type="email" name="email" value={formData?.email || ''} onChange={handleChange} className="form-input" />
            </div>
            <div>
                <label className="form-label">Mobile Number</label>
                <input type="tel" name="contact" value={formData?.contact || ''} onChange={handleChange} className="form-input" maxLength="15" />
            </div>
        </div>
    );

    const renderEducationForm = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="form-label">Education</label>
                <select name="education" value={getSelectedEducationId()} onChange={handleChange} className="form-input">
                    <option value="">Select Education</option>
                    {educationOptions.map(edu => (
                        <option key={edu.id} value={edu.id}>{edu.name}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="form-label">Occupation</label>
                <select name="occupation" value={getSelectedOccupationId()} onChange={handleChange} className="form-input">
                    <option value="">Select Occupation</option>
                    {professionOptions.map(occ => (
                        <option key={occ.id} value={occ.id}>{occ.name}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="form-label">Monthly Income</label>
                <select name="monthaly_income" value={formData?.monthaly_income || ''} onChange={handleChange} className="form-input">
                    <option value="">Select Monthly Income</option>
                    {monthlyIncomeOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="form-label">Company Name</label>
                <input type="text" name="employee_in" value={formData?.employee_in || ''} onChange={handleChange} className="form-input" />
            </div>
            <div>
                <label className="form-label">Designation</label>
                <input type="text" name="designation" value={formData?.designation || ''} onChange={handleChange} className="form-input" />
            </div>
        </div>
    );

    const renderFamilyForm = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="form-label">Father's Name</label><input type="text" name="father" value={formData?.father || ''} onChange={handleChange} className="form-input" /></div>
            <div><label className="form-label">Father's Occupation</label><input type="text" name="father_occupation" value={formData?.father_occupation || ''} onChange={handleChange} className="form-input" /></div>
            <div><label className="form-label">Mother's Name</label><input type="text" name="mother" value={formData?.mother || ''} onChange={handleChange} className="form-input" /></div>
            <div><label className="form-label">Mother's Occupation</label><input type="text" name="mother_occupation" value={formData?.mother_occupation || ''} onChange={handleChange} className="form-input" /></div>
            <div><label className="form-label">Unmarried Sisters</label><input type="number" name="unmarride_sister" value={formData?.unmarride_sister || 0} onChange={handleChange} className="form-input" /></div>
            <div><label className="form-label">Married Sisters</label><input type="number" name="marride_sister" value={formData?.marride_sister || 0} onChange={handleChange} className="form-input" /></div>
            <div><label className="form-label">Unmarried Brothers</label><input type="number" name="unmarride_brother" value={formData?.unmarride_brother || 0} onChange={handleChange} className="form-input" /></div>
            <div><label className="form-label">Married Brothers</label><input type="number" name="marride_brother" value={formData?.marride_brother || 0} onChange={handleChange} className="form-input" /></div>
        </div>
    );

    const renderAppearanceForm = () => {
        const getSelectedHeightValue = () => {
            if (formData?.height) {
                if (!isNaN(formData.height) && formData.height !== '') {
                    return formData.height;
                }
                if (typeof formData.height === 'string' && formData.height.includes('ft')) {
                    const feetMatch = formData.height.match(/(\d+)ft/);
                    const inchesMatch = formData.height.match(/(\d+)in/);
                    if (feetMatch && inchesMatch) {
                        const feet = parseInt(feetMatch[1]);
                        const inches = parseInt(inchesMatch[1]);
                        return (feet * 12) + inches;
                    }
                }
            }
            return '';
        };
        
        const getSelectedWeightValue = () => {
            if (formData?.weight) {
                if (!isNaN(formData.weight) && formData.weight !== '') {
                    return formData.weight;
                }
                if (typeof formData.weight === 'string' && formData.weight.includes('kg')) {
                    const kgMatch = formData.weight.match(/(\d+)/);
                    if (kgMatch) return kgMatch[1];
                }
            }
            return '';
        };
        
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="form-label">Height</label>
                    <select
                        name="height"
                        value={getSelectedHeightValue()}
                        onChange={handleChange}
                        className="form-input"
                    >
                        <option value="">Select Height</option>
                        {heightOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="form-label">Weight (kg)</label>
                    <select
                        name="weight"
                        value={getSelectedWeightValue()}
                        onChange={handleChange}
                        className="form-input"
                    >
                        <option value="">Select Weight</option>
                        {weightOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="md:col-span-2">
                    <label className="form-label">Skin Tone</label>
                    <select name="skin_tone" value={formData?.skin_tone || ''} onChange={handleChange} className="form-input">
                        <option value="">Select Skin Tone</option>
                        <option value="fair">Fair</option>
                        <option value="wheatish">Wheatish</option>
                        <option value="medium">Medium</option>
                        <option value="olive">Olive</option>
                        <option value="brown">Brown</option>
                        <option value="dark">Dark</option>
                    </select>
                </div>
            </div>
        );
    };

    const renderPartnerForm = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="form-label">Looking For</label>
                <select name="looking_for" value={formData?.looking_for || ''} onChange={handleChange} className="form-input">
                    <option value="">Select Looking For</option>
                    <option value="Life Partner">Life Partner</option>
                    <option value="Friend">Friend</option>
                    <option value="Dating">Dating</option>
                    <option value="Marriage">Marriage</option>
                </select>
            </div>
            <div>
                <label className="form-label">Partner's Marital Status</label>
                <select name="marit" value={formData?.marit || ''} onChange={handleChange} className="form-input">
                    <option value="">Select Marital Status</option>
                    <option value="Never Married">Never Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                    <option value="Separated">Separated</option>
                    <option value="Any">Any</option>
                </select>
            </div>
            <div>
                <label className="form-label">Age Gap</label>
                <select name="age" value={formData?.age || ''} onChange={handleChange} className="form-input">
                    <option value="">Select Age Gap</option>
                    <option value="Same Age">Same Age</option>
                    <option value="1-2 Years Younger">1-2 Years Younger</option>
                    <option value="3-5 Years Younger">3-5 Years Younger</option>
                    <option value="5-10 Years Younger">5-10 Years Younger</option>
                    <option value="1-2 Years Older">1-2 Years Older</option>
                    <option value="3-5 Years Older">3-5 Years Older</option>
                    <option value="5-10 Years Older">5-10 Years Older</option>
                    <option value="Any Age">Any Age</option>
                </select>
            </div>
            <div>
                <label className="form-label">Preferred Location</label>
                <select name="location" value={formData?.location || ''} onChange={handleChange} className="form-input">
                    <option value="">Select Location</option>
                    <option value="Around My Locality">Around My Locality</option>
                    <option value="Within Nepal">Within Nepal</option>
                    <option value="Abroad">Abroad</option>
                    <option value="Within Nepal or Abroad Both">Within Nepal or Abroad Both</option>
                    <option value="Anywhere">Anywhere</option>
                </select>
            </div>
            <div>
                <label className="form-label">Partner's Education</label>
                <select name="partner_education" value={formData?.partner_education || ''} onChange={handleChange} className="form-input">
                    <option value="">Select Education</option>
                    <option value="High School">High School</option>
                    <option value="Bachelor's Degree">Bachelor's Degree</option>
                    <option value="Master's Degree">Master's Degree</option>
                    <option value="PhD">PhD</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Any Graduate">Any Graduate</option>
                    <option value="Any Post Graduate">Any Post Graduate</option>
                    <option value="Not Required">Not Required</option>
                </select>
            </div>
            <div>
                <label className="form-label">Partner's Occupation</label>
                <select name="partner_occupation" value={formData?.partner_occupation || ''} onChange={handleChange} className="form-input">
                    <option value="">Select Occupation</option>
                    <option value="Government Employee">Government Employee</option>
                    <option value="Private Employee">Private Employee</option>
                    <option value="Business">Business</option>
                    <option value="Doctor">Doctor</option>
                    <option value="Engineer">Engineer</option>
                    <option value="Teacher">Teacher</option>
                    <option value="Lawyer">Lawyer</option>
                    <option value="Banker">Banker</option>
                    <option value="Software Professional">Software Professional</option>
                    <option value="Student">Student</option>
                    <option value="Not Working">Not Working</option>
                    <option value="Any">Any</option>
                </select>
            </div>
        </div>
    );

    const renderForm = () => {
        switch(section) {
            case 'personal': return renderPersonalForm();
            case 'contact': return renderContactForm();
            case 'education': return renderEducationForm();
            case 'family': return renderFamilyForm();
            case 'appearance': return renderAppearanceForm();
            case 'partner': return renderPartnerForm();
            default: return null;
        }
    };

    const getTitle = () => {
        const titles = {
            personal: 'Edit Personal Details',
            contact: 'Edit Contact Information',
            education: 'Edit Education & Career',
            family: 'Edit Family Details',
            appearance: 'Edit Appearance',
            partner: 'Edit Partner Preferences'
        };
        return titles[section] || 'Edit Details';
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-800">{getTitle()}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <FaTimes />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6">
                    {renderForm()}
                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading} className="btn-primary flex items-center space-x-2">
                            {loading ? <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div> : <FaSave />}
                            <span>Save Changes</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditUserModal;